// src/screens/SentenceCloze.jsx
import React, { useEffect, useMemo, useState } from "react";
import "../App.css";
import { supabase } from "../supabaseClient";
import { loadSentences } from "../data/loadSentences";
import { loadVerbFormsForCloze } from "../data/loadConjugations";
import { loadTopics } from "../data/loadTopics";
import { loadFunctionWords } from "../data/loadConcepts";
import SpeakButton from "../components/SpeakButton";
import {
  Button,
  Note,
  Panel,
  ScreenHeader,
  Stack,
  Tile,
  TileTray,
} from "../components/ui";
import {
  tokenizeTarget,
  analyzeTokens,
  buildSpanishVerbFormsSet,
  isGluedToken,
  joinsPrevious,
  normalizeToken,
} from "../utils/sentenceClozeHelpers";
import { pickSentenceForCloze } from "../utils/clozeSelectionHelpers";
import {
  filterSentencesForWeek,
  buildKnownWordsSet,
  getKnownTopicWords,
  getTargetDifficulty,
} from "../utils/sentencePool";
import { loadWeeklyWordsUpToWeek } from "../data/loadWeeklyWords";
import { languageLabel, parseLanguageId, PERSON_SLOTS } from "../data/languages";
import { TARGET_WORDS, PACE_YEARS } from "../data/learningConfig";
import {
  loadSentenceClozeProgress,
  saveSentenceClozeProgress,
} from "../data/sentenceClozeProgressStorage";

// Key für persistente Satz-History
function getSentenceHistoryKey(languageId, profileId) {
  const lang = languageId || "generic";
  const profile = profileId || "default";
  return `fluent_sentence_history_${profile}_${lang}`;
}

// -------- Hilfsfunktionen für Satzzeichen-Handling --------

function splitLeadingPunctuation(token) {
  if (!token) return { lead: "", core: "" };
  const match = token.match(/^([¿¡"«“([]+)(.*)$/u);
  if (!match) return { lead: "", core: token };
  return { lead: match[1], core: match[2] };
}

function splitTrailingPunctuation(token) {
  if (!token) return { word: "", punct: "" };
  const match = token.match(/^(.*?)([.,;:!?!"»”…)\]]+)$/u);
  if (!match) return { word: token, punct: "" };
  return { word: match[1], punct: match[2] };
}

// -------- Lücken und Wortbank --------

// Ablenker für Artikel-Lücken: der jeweils andere Artikel, je Lernsprache
const ARTICLE_SWAPS = {
  es: {
    el: "la",
    la: "el",
    los: "las",
    las: "los",
    un: "una",
    una: "un",
    unos: "unas",
    unas: "unos",
  },
  fr: {
    le: "la",
    la: "le",
    un: "une",
    une: "un",
    du: "des",
    des: "du",
    au: "aux",
    aux: "au",
  },
};

// So viele falsche Kärtchen kommen höchstens zusätzlich in die Wortbank
const MAX_DISTRACTORS = 2;

function shuffleArray(arr) {
  const copy = [...arr];
  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
}

function wordOfToken(token) {
  const { core } = splitLeadingPunctuation(token);
  const { word } = splitTrailingPunctuation(core);
  return word || core || token;
}

function lowerFirst(word) {
  return word ? word.charAt(0).toLowerCase() + word.slice(1) : word;
}

function upperFirst(word) {
  return word ? word.charAt(0).toUpperCase() + word.slice(1) : word;
}

/** Anzahl Lücken nach Satzlänge: etwa jedes dritte Wort, mindestens 1, höchstens 4 */
function gapCountForLength(wordCount) {
  return Math.max(1, Math.min(4, Math.round(wordCount / 3)));
}

/**
 * Lücken wählen: zuerst Verben, dann Inhaltswörter, dann Artikel/Präpositionen.
 * Verkürzte und angehängte Teile (j', l', -tu) bekommen keine Lücke.
 */
function chooseGapIndices(tokens, roles) {
  const wordIndices = tokens
    .map((_, idx) => idx)
    .filter((idx) => normalizeToken(tokens[idx]) && !isGluedToken(tokens[idx]));
  const byRole = (role) =>
    shuffleArray(wordIndices.filter((idx) => roles[idx] === role));
  const ordered = [
    ...byRole("verb"),
    ...byRole("content"),
    ...byRole("article"),
    ...byRole("prep"),
    ...byRole("other"),
  ];
  return ordered
    .slice(0, gapCountForLength(wordIndices.length))
    .sort((a, b) => a - b);
}

/**
 * Erwartetes Wort einer Lücke. Am Satzanfang klein geschrieben, damit die Großschreibung
 * nicht verrät, wohin das Kärtchen gehört – außer bei Namen, die auch im deutschen Satz stehen.
 */
function answerForGap(tokens, idx, germanSentence) {
  const word = wordOfToken(tokens[idx]);
  if (idx !== 0) return word;
  const isName = (germanSentence || "").split(/[\s.,;:!?¿¡"]+/).includes(word);
  return isName ? word : lowerFirst(word);
}

/** Falsche Kärtchen: eine andere Form desselben Verbs (hablas statt habla) oder der andere Artikel */
function buildDistractors(gaps, answers, roles, verbFamilies, lang) {
  const articleSwaps = ARTICLE_SWAPS[lang] || ARTICLE_SWAPS.es;
  const taken = new Set(gaps.map((idx) => answers[idx].toLowerCase()));
  const result = [];
  const add = (word) => {
    if (!word || taken.has(word.toLowerCase())) return;
    taken.add(word.toLowerCase());
    result.push(word);
  };

  for (const idx of shuffleArray(gaps)) {
    if (result.length >= MAX_DISTRACTORS) break;
    const answer = answers[idx];
    if (roles[idx] === "verb") {
      const family = verbFamilies.get(normalizeToken(answer)) || [];
      add(shuffleArray(family).find((form) => !taken.has(form.toLowerCase())));
    } else if (roles[idx] === "article") {
      add(articleSwaps[answer.toLowerCase()]);
    }
  }
  return result;
}

export default function SentenceCloze({
  language,
  pace,
  profileId,
  weekIndex,
  onBack,
}) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [allSentences, setAllSentences] = useState([]);
  const [sentence, setSentence] = useState(null);

  const [tokens, setTokens] = useState([]);
  const [gapIndices, setGapIndices] = useState([]);
  const [slotValues, setSlotValues] = useState([]);
  const [tiles, setTiles] = useState([]);
  const [checked, setChecked] = useState(false);
  const [correctMap, setCorrectMap] = useState({});

  const [spanishVerbs, setSpanishVerbs] = useState([]);
  // Funktionswörter der Lernsprache (que, en, a, y, no …) – gelten bei der Satzauswahl als bekannt
  const [functionWords, setFunctionWords] = useState(new Set());
  const [verbFormsSet, setVerbFormsSet] = useState(null);
  // Themen-Einträge (Zahlen, Uhrzeit …), ihre Wörter gelten nach der Themen-Woche als bekannt
  const [topics, setTopics] = useState([]);

  const [weeklySetAllWeeks, setWeeklySetAllWeeks] = useState([]);

  const [recentSentenceIds, setRecentSentenceIds] = useState([]);
  const RECENT_LIMIT = 15;

  const [historyReady, setHistoryReady] = useState(false);



  // Erwartetes Wort je Lücke (Index → Wort)
  const [answers, setAnswers] = useState({});
  // 0 = erster Versuch, 1 = zweiter Versuch (richtige Lücken sind dann fest)
  const [attempt, setAttempt] = useState(0);
  const [lockedGaps, setLockedGaps] = useState([]);

  // Intro-Overlay mit Fade-Out
  const [showIntroOverlay, setShowIntroOverlay] = useState(true);
  const [introOpaque, setIntroOpaque] = useState(true);

  // Progress: wie viele Sätze in dieser Konfiguration korrekt gelöst
  const [correctCount, setCorrectCount] = useState(0);

  const langId = language || "es-de";
  const paceValue = pace || "medium";
  const profile = profileId || "default";

  const years = PACE_YEARS[paceValue] ?? PACE_YEARS.medium;
  const weeksTotal = Math.round(years * 52);
  const weeklyWords = Math.ceil(TARGET_WORDS / weeksTotal);

  const topicWords = useMemo(
    () => getKnownTopicWords(topics, weekIndex || 1, paceValue, parseLanguageId(langId)?.source),
    [topics, weekIndex, paceValue, langId]
  );
  const knownWordsSet = useMemo(
    () => buildKnownWordsSet(weeklySetAllWeeks, spanishVerbs, functionWords, langId, topicWords),
    [weeklySetAllWeeks, spanishVerbs, functionWords, langId, topicWords]
  );

  function shuffle(arr) {
    const copy = [...arr];
    for (let i = copy.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [copy[i], copy[j]] = [copy[j], copy[i]];
    }
    return copy;
  }

  function registerSentence(nextSentence) {
    setSentence(nextSentence);
    if (!nextSentence || !nextSentence.id) return;

    setRecentSentenceIds((prev) => {
      const withNew = [...prev, nextSentence.id];
      const trimmed =
        withNew.length <= RECENT_LIMIT
          ? withNew
          : withNew.slice(withNew.length - RECENT_LIMIT);

      if (typeof window !== "undefined") {
        try {
          const key = getSentenceHistoryKey(langId, profile);
          window.localStorage.setItem(key, JSON.stringify(trimmed));
        } catch (e) {
          console.warn("SentenceCloze: persist history failed:", e);
        }
      }

      return trimmed;
    });
  }

  // Verbformen laden
  useEffect(() => {
    let cancelled = false;

    async function loadVerbForms() {
      try {
        // Präsensformen aller Verben der Lernsprache (Konjugationsdateien, über Concept-ID)
        const sourceLang = parseLanguageId(langId)?.source || "es";
        const [verbs, funcWords, topicEntries] = await Promise.all([
          loadVerbFormsForCloze(sourceLang),
          loadFunctionWords(sourceLang),
          // fehlen die Themen der Lernsprache, geht es ohne weiter
          loadTopics(sourceLang).catch(() => []),
        ]);
        if (cancelled) return;
        setSpanishVerbs(verbs);
        setFunctionWords(funcWords);
        setTopics(topicEntries);
        setVerbFormsSet(buildSpanishVerbFormsSet(verbs));
      } catch (e) {
        console.error(
          "SentenceCloze: Fehler beim Laden der spanischen Verben:",
          e
        );
        if (!cancelled) {
          setSpanishVerbs([]);
          setVerbFormsSet(new Set());
        }
      }
    }

    loadVerbForms();
    return () => {
      cancelled = true;
    };
  }, [langId]);

  // Sätze aus CSV laden
  useEffect(() => {
    let cancelled = false;

    async function init() {
      setLoading(true);
      setError(null);
      try {
        const learningLang = parseLanguageId(langId)?.source || "es";
        const sentences = await loadSentences(learningLang);
        if (cancelled) return;
        setAllSentences(sentences);
        if (sentences.length === 0) {
          setError(`Für ${languageLabel(learningLang)} gibt es noch keine Sätze.`);
        }
      } catch (e) {
        console.error("SentenceCloze: loadSentences failed:", e);
        if (!cancelled) {
          setError(e.message || "Unbekannter Fehler beim Laden.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    init();
    return () => {
      cancelled = true;
    };
  }, [langId]);

  // Weekly-Sets laden
  useEffect(() => {
    let cancelled = false;

    async function loadWeeklySetsAll() {
      const allWords = await loadWeeklyWordsUpToWeek({
        langId,
        pace: paceValue,
        weeklyWords,
        profile,
        weekIndex,
      });
      if (!cancelled) {
        setWeeklySetAllWeeks(allWords);
      }
    }

    loadWeeklySetsAll();
    return () => {
      cancelled = true;
    };
  }, [langId, paceValue, weeklyWords, profile, weekIndex]);

  // Satz-History laden
  useEffect(() => {
    if (typeof window === "undefined") {
      setHistoryReady(true);
      return;
    }
    try {
      const key = getSentenceHistoryKey(langId, profile);
      const raw = window.localStorage.getItem(key);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) {
          setRecentSentenceIds(parsed);
        }
      }
    } catch (e) {
      console.warn("SentenceCloze: load sentence history failed:", e);
    } finally {
      setHistoryReady(true);
    }
  }, [langId, profile]);

  // Progress laden (aus sentence_cloze_progress in Supabase)
  useEffect(() => {
    let cancelled = false;

    async function loadProgress() {
      try {
        const { data: userData, error: userError } =
          await supabase.auth.getUser();

        if (userError || !userData?.user?.id) {
          if (!cancelled) setCorrectCount(0);
          return;
        }

        const userId = userData.user.id;

        // Nur der Zähler der geöffneten Woche – jede Woche zählt für sich
        const value = await loadSentenceClozeProgress(
          userId,
          profile,
          langId,
          paceValue,
          weekIndex || 1
        );

        if (!cancelled && typeof value === "number" && !Number.isNaN(value)) {
          setCorrectCount(value);
        }
      } catch (e) {
        console.warn("SentenceCloze: loadSentenceClozeProgress failed:", e);
        if (!cancelled) setCorrectCount(0);
      }
    }

    loadProgress();
    return () => {
      cancelled = true;
    };
  }, [profile, langId, paceValue, weekIndex]);

  // Intro-Overlay: erst voll sichtbar, dann 0.5s Fade-Out, dann unmount
  useEffect(() => {
    const fadeTimer = setTimeout(() => {
      setIntroOpaque(false);
    }, 500); // nach 0.5s → Fade starten

    const hideTimer = setTimeout(() => {
      setShowIntroOverlay(false);
    }, 1000); // nach 1.0s → Overlay entfernen

    return () => {
      clearTimeout(fadeTimer);
      clearTimeout(hideTimer);
    };
  }, []);

  // Satz auswählen (nur wenn noch keiner gesetzt ist)
  useEffect(() => {
    if (!historyReady) return;
    if (!verbFormsSet || allSentences.length === 0) return;
    if (sentence) return;

    const pool = filterSentencesForWeek(
      allSentences,
      weekIndex,
      paceValue,
      verbFormsSet,
      recentSentenceIds,
      parseLanguageId(langId)?.source
    );

    const picked = pickSentenceForCloze(pool, knownWordsSet, {
      minKnownRatio: 0.9,
      relaxKnownRatio: 0.8,
      hardMinKnownRatio: 0.7,
      maxDifficulty: 1.0,
      targetDifficulty: getTargetDifficulty(weekIndex || 1, paceValue),
    });

    if (!picked) {
      setError("Keine passenden Sätze für dein aktuelles Niveau gefunden.");
      return;
    }

    registerSentence(picked);
  }, [
    historyReady,
    verbFormsSet,
    allSentences,
    knownWordsSet,
    weekIndex,
    paceValue,
    sentence,
    recentSentenceIds,
    langId,
  ]);

  // Verbform → alle Präsensformen desselben Verbs (für falsche Kärtchen, z. B. "hablas" zu "habla")
  const verbFamilies = useMemo(() => {
    const map = new Map();
    for (const verb of spanishVerbs || []) {
      const forms = PERSON_SLOTS.map((slot) => verb[slot]).filter(Boolean);
      for (const form of forms) {
        const key = normalizeToken(form);
        if (key && !map.has(key)) map.set(key, forms);
      }
    }
    return map;
  }, [spanishVerbs]);

  // Satz → Tokens, Lücken, Wortbank
  useEffect(() => {
    if (!sentence || !verbFormsSet) return;

    const lang = parseLanguageId(langId)?.source || "es";
    const t = tokenizeTarget(sentence.target, lang);
    const roles = analyzeTokens(t, verbFormsSet, lang);
    const gaps = chooseGapIndices(t, roles);

    const expected = {};
    gaps.forEach((idx) => {
      expected[idx] = answerForGap(t, idx, sentence.src);
    });
    const distractors = buildDistractors(gaps, expected, roles, verbFamilies, lang);

    setTokens(t);
    setGapIndices(gaps);
    setAnswers(expected);
    setAttempt(0);
    setLockedGaps([]);
    setChecked(false);
    setCorrectMap({});
    setSlotValues(t.map((token, idx) => (gaps.includes(idx) ? null : token)));
    setTiles(shuffle([...gaps.map((idx) => expected[idx]), ...distractors]));
  }, [sentence, verbFormsSet, verbFamilies, langId]);

  const handleTileClick = (tileToken) => {
    if (checked) return;

    const emptyGapIndex = gapIndices.find((idx) => slotValues[idx] === null);
    if (emptyGapIndex === undefined) return;

    const newTiles = [...tiles];
    const tileIdx = newTiles.indexOf(tileToken);
    if (tileIdx !== -1) {
      newTiles.splice(tileIdx, 1);
    }

    const newSlots = [...slotValues];
    newSlots[emptyGapIndex] = tileToken;

    setTiles(newTiles);
    setSlotValues(newSlots);
  };

  const handleGapClick = (idx) => {
    if (checked || lockedGaps.includes(idx)) return;
    if (!gapIndices.includes(idx)) return;
    const current = slotValues[idx];
    if (current == null) return;

    setTiles((prev) => [...prev, current]);

    setSlotValues((prev) => {
      const copy = [...prev];
      copy[idx] = null;
      return copy;
    });
  };

  const handleCheck = async () => {
    if (!sentence || checked) return;

    const correct = {};
    let allCorrect = true;

    gapIndices.forEach((idx) => {
      const isCorrect = slotValues[idx] === answers[idx];
      correct[idx] = isCorrect;
      if (!isCorrect) allCorrect = false;
    });

    // Erster Fehlversuch: richtige Lücken bleiben (grün, fest), falsche Kärtchen zurück in die Wortbank
    if (!allCorrect && attempt === 0) {
      const slots = [...slotValues];
      const back = [];
      gapIndices.forEach((idx) => {
        if (!correct[idx] && slots[idx] != null) {
          back.push(slots[idx]);
          slots[idx] = null;
        }
      });
      setLockedGaps(gapIndices.filter((idx) => correct[idx]));
      setSlotValues(slots);
      setTiles((prev) => shuffle([...prev, ...back]));
      setAttempt(1);
      return;
    }

    setCorrectMap(correct);
    setChecked(true);

    if (allCorrect) {
      // Progress hochzählen und in Supabase speichern
      try {
        const { data: userData, error: userError } =
          await supabase.auth.getUser();

        const newCount = correctCount + 1;
        setCorrectCount(newCount);

        if (!userError && userData?.user?.id) {
          const userId = userData.user.id;
          await saveSentenceClozeProgress(
            userId,
            profile,
            langId,
            paceValue,
            newCount,
            weekIndex || 1
          );
        } else {
          // nicht eingeloggt → nur lokal im State hochzählen
          console.warn(
            "SentenceCloze: no userId available, skipping Supabase save."
          );
        }
      } catch (e) {
        console.warn("SentenceCloze: saveSentenceClozeProgress failed:", e);
      }
    }
  };

  const handleNext = () => {
    if (!allSentences || allSentences.length === 0) return;

    setChecked(false);
    setCorrectMap({});
    setTokens([]);
    setGapIndices([]);
    setSlotValues([]);
    setTiles([]);

    const pool = filterSentencesForWeek(
      allSentences,
      weekIndex,
      paceValue,
      verbFormsSet,
      recentSentenceIds,
      parseLanguageId(langId)?.source
    );

    const next = pickSentenceForCloze(pool, knownWordsSet, {
      minKnownRatio: 0.9,
      relaxKnownRatio: 0.8,
      hardMinKnownRatio: 0.7,
      maxDifficulty: 1.0,
      targetDifficulty: getTargetDifficulty(weekIndex || 1, paceValue),
    });

    if (!next) {
      setError("Keine weiteren passenden Sätze gefunden.");
      return;
    }

    registerSentence(next);
  };

  const title = `Lückentexte – Woche ${weekIndex || 1}`;
  const backButton = (
    <Button block variant="secondary" onClick={onBack}>
      Zurück zum Hauptmenü
    </Button>
  );

  const mainButtonLabel = checked
    ? "Nächster Satz"
    : attempt === 1
    ? "Nochmal prüfen"
    : "Fertig";
  const mainButtonOnClick = checked ? handleNext : handleCheck;

  if (loading || !verbFormsSet || !historyReady) {
    return (
      <>
        <ScreenHeader title={title} />
        <Note>fluent! lädt deine Lückentexte …</Note>
        <div className="exercise-actions">{backButton}</div>
      </>
    );
  }

  if (error) {
    return (
      <>
        <ScreenHeader title={title} />
        <Note tone="error">Fehler: {error}</Note>
        <div className="exercise-actions">{backButton}</div>
      </>
    );
  }

  if (!sentence) {
    return (
      <>
        <ScreenHeader title={title} />
        <Note>Kein passender Satz gefunden.</Note>
        <div className="exercise-actions">{backButton}</div>
      </>
    );
  }

  const showCorrectionForGap = (idx) =>
    checked &&
    Object.prototype.hasOwnProperty.call(correctMap, idx) &&
    !correctMap[idx];

  const allCorrect = checked && Object.values(correctMap).every(Boolean);

  // Token-Indizes je angezeigtem Wort (im Spanischen immer ein Token pro Wort)
  const tokenGroups = [];
  tokens.forEach((_, idx) => {
    if (joinsPrevious(tokens, idx) && tokenGroups.length > 0) tokenGroups[tokenGroups.length - 1].push(idx);
    else tokenGroups.push([idx]);
  });

  return (
    <div style={{ position: "relative" }}>
      {/* Intro-Overlay: voll weiß + 0.5s Fade-Out */}
      {showIntroOverlay && (
        <div className="cloze-overlay" style={{ opacity: introOpaque ? 1 : 0 }}>
          <div className="cloze-spinner" />
          <Note>Lückentext wird vorbereitet…</Note>
        </div>
      )}

      <ScreenHeader title={title} subtitle="Ergänze die Lücken mit den Kärtchen" />

      {/* Deutscher Satz */}
      <Panel variant="muted" className="exercise-prompt">
        <div className="exercise-prompt__text">{sentence.src}</div>
      </Panel>

      <Stack gap={3}>
        {/* Spanischer Lückentext */}
        <TileTray variant="answer" className="cloze-sentence">
          {tokenGroups.map((group) => (
            // Teile eines Worts (j' + ai, viens + -tu) ohne Leerzeichen in einer Gruppe
            <span key={group[0]} className="cloze-word">
              {group.map((idx) => {
                const token = tokens[idx];
                const isGap = gapIndices.includes(idx);

                const { lead, core } = splitLeadingPunctuation(token);
                const { word, punct } = splitTrailingPunctuation(core);
                const value = slotValues[idx];

                const rawTrimmed = (token || "").trim();
                if (!isGap && !lead && !word && !punct && rawTrimmed === "") {
                  return null;
                }

                if (!isGap) {
                  return (
                    <React.Fragment key={idx}>
                      {lead}
                      {word}
                      {punct}
                    </React.Fragment>
                  );
                }

                const isCorrect = correctMap[idx];
                const isWrong = showCorrectionForGap(idx);
                const isLocked = lockedGaps.includes(idx);
                const shown = idx === 0 ? upperFirst(value) : value;

                let tone;
                if ((checked && isCorrect) || (!checked && isLocked)) tone = "right";
                else if (isWrong) tone = "wrong";

                return (
                  <React.Fragment key={idx}>
                    {lead}
                    <Tile
                      className="cloze-slot"
                      empty={!shown}
                      tone={tone}
                      onClick={() => handleGapClick(idx)}
                    >
                      {shown}
                    </Tile>
                    {punct}
                  </React.Fragment>
                );
              })}
            </span>
          ))}
        </TileTray>

        {/* Wortkärtchen */}
        <TileTray variant="pool">
          {tiles.map((tile, idx) => (
            <Tile
              key={`${tile}-${idx}`}
              className="cloze-slot"
              onClick={() => handleTileClick(tile)}
            >
              {tile}
            </Tile>
          ))}
        </TileTray>

        {attempt === 1 && !checked && (
          <Note className="exercise-feedback">
            Fast! Die grünen Lücken stimmen – versuch die übrigen nochmal.
          </Note>
        )}

        {/* Ganzer Satz nach dem Prüfen, zum Anhören */}
        {checked && (
          <div className="cloze-solution">
            <Note tone={allCorrect ? "success" : "info"} className="cloze-solution__text">
              {sentence.target}
            </Note>
            <SpeakButton
              text={sentence.target}
              lang={parseLanguageId(langId)?.source || "es"}
            />
          </div>
        )}

        <Button block onClick={mainButtonOnClick}>
          {mainButtonLabel}
        </Button>
      </Stack>

      <div className="exercise-actions-tight">{backButton}</div>

    </div>
  );
}
